/**
 * @fileoverview Canvas HTML -> PDF Blob
 *
 * Lightweight HTML-to-PDF generator for the universal-agent Canvas
 * surface. Used by the "Save to job" button on `CanvasPanel`.
 *
 * Approach:
 * - Build a hidden iframe sized to A4 dimensions.
 * - Drop the canvas HTML inside, styled like the preview the user
 *   already sees on screen.
 * - Render each page-sized slice with `html2canvas` and stitch them
 *   into a multi-page PDF via `jspdf`.
 *
 * Trade-off: the output is image-based (no selectable text). That's
 * acceptable for a "save as file note" capture — fidelity matters more
 * than copy-paste here. If selectable text becomes important later,
 * swap in `jspdf.html()` or a server-side renderer.
 */

import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { ChartRenderer } from "@/components/agents/universal/chartRenderer";
import { chartSpecFromAttr } from "@/components/agents/universal/canvas/chartHtml";
import {
  spreadsheetSpecFromAttr,
  spreadsheetToTableHtml,
} from "@/components/agents/universal/canvas/spreadsheetHtml";
import {
  signatureSpecFromAttr,
  signatureToHtml,
} from "@/components/agents/universal/canvas/signatureHtml";
import { diagramSpecFromAttr } from "@/components/agents/universal/canvas/diagramHtml";
import {
  mermaidSvgToPngDataUrl,
  renderMermaidSvg,
} from "@/components/agents/universal/mermaid";

const A4_WIDTH_PX = 794; // 8.27in × 96dpi
const A4_HEIGHT_PX = 1123; // 11.69in × 96dpi
const PAGE_PADDING_PX = 48;

const CANVAS_PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111; }
  .canvas-print-page {
    width: ${A4_WIDTH_PX}px;
    min-height: ${A4_HEIGHT_PX}px;
    padding: ${PAGE_PADDING_PX}px;
    background: #ffffff;
    box-sizing: border-box;
  }
  .canvas-print-page h1 { font-size: 22px; font-weight: 600; margin: 0 0 12px; }
  .canvas-print-page h2 { font-size: 18px; font-weight: 600; margin: 18px 0 8px; }
  .canvas-print-page h3 { font-size: 15px; font-weight: 600; margin: 14px 0 6px; }
  .canvas-print-page p { font-size: 12px; line-height: 1.55; margin: 0 0 10px; }
  .canvas-print-page ul, .canvas-print-page ol { font-size: 12px; line-height: 1.55; margin: 0 0 10px 22px; padding: 0; }
  .canvas-print-page li { margin-bottom: 4px; }
  .canvas-print-page table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px; }
  .canvas-print-page th, .canvas-print-page td { border: 1px solid #d0d0d0; padding: 6px 8px; vertical-align: top; }
  .canvas-print-page th { background: #f4f4f4; font-weight: 600; text-align: left; }
  .canvas-print-page strong { font-weight: 600; }
  .canvas-print-page em { font-style: italic; }
  .canvas-print-page hr { border: 0; border-top: 1px solid #ddd; margin: 14px 0; }
  .canvas-print-page img { max-width: 100%; height: auto; }
`;

interface CanvasToPdfArgs {
  /** TipTap HTML body of the canvas. */
  html: string;
  /** Title shown at the top of page 1. */
  title: string;
  /**
   * Optional footer line (e.g. preparer name + date) drawn in the bottom
   * margin of every page.
   */
  footer?: string;
}

/**
 * Render the canvas HTML to a multi-page PDF blob. Loads `html2canvas`
 * + `jspdf` dynamically so they only ship in the bundle when the user
 * actually clicks "Save to job".
 */
export async function canvasToPdfBlob({
  html,
  title,
  footer,
}: CanvasToPdfArgs): Promise<Blob> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.left = "-99999px";
  iframe.style.top = "0";
  iframe.style.width = `${A4_WIDTH_PX}px`;
  iframe.style.height = `${A4_HEIGHT_PX}px`;
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  try {
    const printWin = iframe.contentWindow;
    const printDoc = iframe.contentDocument;
    if (!printWin || !printDoc) {
      throw new Error("canvasToPdfBlob: iframe document unavailable");
    }

    // We render the whole canvas in one tall page wrapper, then slice
    // the resulting canvas image into A4-sized pages. Simpler than
    // measuring DOM and breaking inside flow elements.
    // Charts and spreadsheets are stored as empty placeholder divs
    // (`data-morgan-chart` / `data-morgan-spreadsheet`) — they only render
    // via React in the live editor. The static print iframe has no React, so
    // replace each placeholder with capturable markup (a rasterised chart
    // image, a real `<table>`) before writing it in.
    const renderedHtml = await renderCanvasBlocksForPrint(html);

    const safeTitle = escapeHtml(title);
    const htmlStartsWithHeading = /^\s*<h[1-3][\s>]/i.test(renderedHtml);
    printDoc.open();
    printDoc.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>${CANVAS_PRINT_CSS}</style></head>
<body><div class="canvas-print-page" id="canvas-render-root">
  ${htmlStartsWithHeading ? "" : `<h1>${safeTitle}</h1>`}
  ${renderedHtml}
</div></body></html>`);
    printDoc.close();

    // Wait for fonts + images to settle so html2canvas captures the
    // final visual.
    await waitForRender(printDoc);

    const rootEl = printDoc.getElementById("canvas-render-root");
    if (!rootEl) throw new Error("canvasToPdfBlob: render root missing");

    // Nudge blocks that would straddle an A4 boundary onto the next page so
    // the fixed-height slicing below never cuts through a line of text or a
    // table row.
    paginateForCapture(rootEl);

    const [{ default: html2canvas }, jsPdfModule] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const JsPDF =
      jsPdfModule.jsPDF ??
      (jsPdfModule as unknown as { default: typeof jsPdfModule.jsPDF }).default;

    const pdf = new JsPDF({
      unit: "px",
      format: [A4_WIDTH_PX, A4_HEIGHT_PX],
      orientation: "portrait",
      hotfixes: ["px_scaling"],
    });

    // Capture at 2x for crispness — but never in one tall canvas. Browsers
    // cap a canvas dimension (Chrome: 65,535px; 32,767px is the safe
    // cross-browser bound) and html2canvas silently returns BLANK pixels
    // past the cap, so a long report captured whole becomes a blank PDF.
    // Instead capture page-aligned chunks that keep the output canvas under
    // the cap, then slice each chunk into A4 pages.
    const CAPTURE_SCALE = 2;
    const MAX_CAPTURE_CANVAS_PX = 32000;
    const pagesPerChunk = Math.max(
      1,
      Math.floor(MAX_CAPTURE_CANVAS_PX / CAPTURE_SCALE / A4_HEIGHT_PX),
    );
    const chunkSrcHeight = pagesPerChunk * A4_HEIGHT_PX;
    const totalHeight = rootEl.scrollHeight;

    let pageIndex = 0;
    for (
      let chunkStart = 0;
      chunkStart < totalHeight;
      chunkStart += chunkSrcHeight
    ) {
      const chunkHeight = Math.min(chunkSrcHeight, totalHeight - chunkStart);
      const chunkCanvas = await html2canvas(rootEl, {
        scale: CAPTURE_SCALE,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: A4_WIDTH_PX,
        windowHeight: totalHeight,
        y: chunkStart,
        height: chunkHeight,
      });

      // Slice this chunk's canvas into A4-height pages.
      const scale = chunkCanvas.width / A4_WIDTH_PX;
      const pageHeightSrc = Math.floor(A4_HEIGHT_PX * scale);
      let yOffset = 0;
      while (yOffset < chunkCanvas.height) {
        const sliceHeight = Math.min(
          pageHeightSrc,
          chunkCanvas.height - yOffset,
        );
        // Skip degenerate slivers from rounding at the chunk boundary.
        if (sliceHeight < 2) break;
        const sliceCanvas = printDoc.createElement("canvas");
        sliceCanvas.width = chunkCanvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext("2d");
        if (!ctx) throw new Error("canvasToPdfBlob: slice context unavailable");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          chunkCanvas,
          0,
          yOffset,
          chunkCanvas.width,
          sliceHeight,
          0,
          0,
          chunkCanvas.width,
          sliceHeight,
        );
        const imgData = sliceCanvas.toDataURL("image/jpeg", 0.92);
        if (pageIndex > 0) pdf.addPage([A4_WIDTH_PX, A4_HEIGHT_PX]);
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0,
          A4_WIDTH_PX,
          Math.round(sliceHeight / scale),
        );
        yOffset += sliceHeight;
        pageIndex += 1;
      }
    }

    // Footer and page numbers live in the bottom margin that
    // `paginateForCapture` keeps clear of content. No AI disclaimer here: the
    // canvas and report PDFs are documents the practitioner circulates, and
    // they would have to strip it by hand.
    const pageCount = pageIndex;
    const footerY = A4_HEIGHT_PX - 20;

    pdf.setFontSize(8);
    pdf.setTextColor(136, 136, 136);
    for (let page = 1; page <= pageCount; page += 1) {
      pdf.setPage(page);

      if (footer) pdf.text(footer, PAGE_PADDING_PX, footerY);
      if (pageCount > 1) {
        pdf.text(
          `Page ${page} of ${pageCount}`,
          A4_WIDTH_PX - PAGE_PADDING_PX,
          footerY,
          { align: "right" },
        );
      }
    }

    const arrayBuffer = pdf.output("arraybuffer");
    return new Blob([arrayBuffer], { type: "application/pdf" });
  } finally {
    iframe.remove();
  }
}

/** Vertical space usable by content on one page (between top/bottom padding). */
const PAGE_CONTENT_HEIGHT_PX = A4_HEIGHT_PX - PAGE_PADDING_PX * 2;

/**
 * Reserve below a heading so it never sits alone at the bottom of a page —
 * roughly one line of the paragraph that follows it.
 */
const HEADING_KEEP_WITH_NEXT_PX = 28;

/** Parents whose children flow as individually breakable blocks. */
const DESCEND_TAGS = new Set([
  "DIV",
  "SECTION",
  "ARTICLE",
  "UL",
  "OL",
  "TABLE",
  "THEAD",
  "TBODY",
  "TFOOT",
  "BLOCKQUOTE",
]);

/**
 * Shift any block that would straddle an A4 page boundary down to the top of
 * the next page, so the canvas-slicing capture never cuts mid-line. Works on
 * the live layout: elements are visited top-to-bottom and re-measured after
 * each shift, so earlier spacing is accounted for automatically. Blocks
 * taller than a full page (huge paragraphs, oversize images) are left alone
 * and still slice — pushing them would just move the cut.
 *
 * Exported for browser-based tests — needs a real layout engine, so it can't
 * be exercised under jsdom.
 */
export function paginateForCapture(root: HTMLElement): void {
  for (const el of collectBlockAtoms(root)) {
    const rootTop = root.getBoundingClientRect().top;
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.height > PAGE_CONTENT_HEIGHT_PX) continue;

    const top = rect.top - rootTop;
    const pageIndex = Math.floor(top / A4_HEIGHT_PX);
    const pageStart = pageIndex * A4_HEIGHT_PX;
    const contentBottom = pageStart + A4_HEIGHT_PX - PAGE_PADDING_PX;
    const keepWithNext = /^H[1-6]$/.test(el.tagName)
      ? HEADING_KEEP_WITH_NEXT_PX
      : 0;

    const crossesBottom = top + rect.height + keepWithNext > contentBottom;
    // Blocks that naturally flow into the next page's top padding get nudged
    // down to the content area so every page starts with a clean margin.
    const inTopPadding = pageIndex > 0 && top < pageStart + PAGE_PADDING_PX;
    if (!crossesBottom && !inTopPadding) continue;

    const targetTop = crossesBottom
      ? pageStart + A4_HEIGHT_PX + PAGE_PADDING_PX
      : pageStart + PAGE_PADDING_PX;
    insertBreakSpacing(el, targetTop - top);
  }
}

/**
 * Depth-first list of breakable blocks in document order. Containers taller
 * than a page are descended into (their children break individually);
 * everything else is treated as an unbreakable atom.
 */
function collectBlockAtoms(
  parent: HTMLElement,
  out: HTMLElement[] = [],
): HTMLElement[] {
  // No `instanceof HTMLElement` here: the elements belong to the print
  // iframe's realm, whose classes are distinct from the caller's, so
  // cross-realm instanceof is always false.
  for (const child of Array.from(parent.children) as HTMLElement[]) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const height = child.getBoundingClientRect().height;
    if (
      height > PAGE_CONTENT_HEIGHT_PX &&
      DESCEND_TAGS.has(child.tagName) &&
      child.children.length > 0
    ) {
      collectBlockAtoms(child, out);
    } else {
      out.push(child);
    }
  }
  return out;
}

/**
 * Push `el` down by `delta` px. Uses whatever spacing mechanism is valid for
 * the element's context: a spacer row inside tables, padding on list items
 * (a spacer div is invalid inside `ul`/`ol`), and a spacer div elsewhere
 * (padding would leave bordered/backgrounded elements like `hr` behind,
 * since their visible box top doesn't move with padding).
 */
function insertBreakSpacing(el: HTMLElement, delta: number): void {
  if (delta <= 0) return;
  const doc = el.ownerDocument;
  const height = `${Math.ceil(delta)}px`;

  // tagName check instead of `instanceof HTMLTableRowElement` — iframe
  // elements come from another realm where instanceof never matches.
  if (el.tagName === "TR") {
    const spacer = doc.createElement("tr");
    spacer.setAttribute("data-pdf-spacer", "");
    const td = doc.createElement("td");
    td.colSpan = Math.max(
      1,
      Array.from((el as HTMLTableRowElement).cells).reduce(
        (n, cell) => n + cell.colSpan,
        0,
      ),
    );
    td.style.cssText = `border:none;padding:0;height:${height};background:#ffffff;`;
    spacer.appendChild(td);
    el.parentElement?.insertBefore(spacer, el);
    return;
  }

  if (el.tagName === "LI") {
    const currentPadding =
      parseFloat(doc.defaultView?.getComputedStyle(el).paddingTop ?? "0") || 0;
    el.style.paddingTop = `${Math.ceil(currentPadding + delta)}px`;
    return;
  }

  const spacer = doc.createElement("div");
  spacer.setAttribute("data-pdf-spacer", "");
  spacer.style.height = height;
  el.parentElement?.insertBefore(spacer, el);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function waitForRender(doc: Document): Promise<void> {
  // Two-tick wait + font readiness gives html2canvas a chance to see
  // the final layout. Mirrors `waitForPrintWindowReady` semantics from
  // `exportHtmlDocument.ts`.
  await new Promise((r) => requestAnimationFrame(() => r(undefined)));
  await new Promise((r) => requestAnimationFrame(() => r(undefined)));
  const fonts = (doc as Document & { fonts?: { ready: Promise<unknown> } })
    .fonts;
  if (fonts?.ready) {
    try {
      await fonts.ready;
    } catch {
      // ignore — render anyway
    }
  }
  await Promise.all(
    Array.from(doc.images).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) return resolve();
          img.addEventListener("load", () => resolve(), { once: true });
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  );
}

/** Content width available inside the A4 page padding. */
const PRINT_CONTENT_WIDTH_PX = A4_WIDTH_PX - PAGE_PADDING_PX * 2;
const PRINT_CHART_HEIGHT_PX = 260;

/**
 * Width for chart images embedded in a Word export. Word's altChunk import
 * ignores percentage/`max-*` sizing and falls back to the PNG's native pixel
 * width (2x capture ≈ 1400px), which overflows the page — so the `word`
 * target emits explicit pixel dimensions instead. 660px fits inside the A4
 * body width at the default 56px side padding (794 − 112 = 682px usable).
 */
const WORD_CHART_WIDTH_PX = 660;

/**
 * Replace the canvas's chart/spreadsheet placeholder divs with static,
 * html2canvas-capturable markup. Charts are rasterised to a PNG `<img>`
 * (recharts can't draw inside the static print iframe); spreadsheets become
 * real `<table>`s (styled by `CANVAS_PRINT_CSS`).
 *
 * The `word` target is used by the report Word export, which needs the same
 * static markup before handing the HTML to `buildWordBlob` (Word can't run
 * React either) but requires explicit image dimensions.
 */
export async function renderCanvasBlocksForPrint(
  html: string,
  target: "print" | "word" = "print",
): Promise<string> {
  const root = document.createElement("div");
  root.innerHTML = html;

  // Signatures — synchronous spec → static image/text markup.
  for (const el of Array.from(
    root.querySelectorAll("div[data-morgan-signature]"),
  )) {
    const spec = signatureSpecFromAttr(
      el.getAttribute("data-morgan-signature") ?? "",
    );
    el.outerHTML = signatureToHtml(spec, target);
  }

  // Spreadsheets — synchronous spec → <table>.
  for (const el of Array.from(
    root.querySelectorAll("div[data-morgan-spreadsheet]"),
  )) {
    const spec = spreadsheetSpecFromAttr(
      el.getAttribute("data-morgan-spreadsheet") ?? "",
    );
    el.outerHTML = spec
      ? spreadsheetToTableHtml(spec)
      : `<p><em>Spreadsheet could not be rendered.</em></p>`;
  }

  // Diagrams — render the Mermaid source to SVG (light theme for print),
  // rasterise to a high-DPI PNG. Native canvas capture — no html2canvas
  // needed since Mermaid already emits an SVG.
  for (const el of Array.from(
    root.querySelectorAll("div[data-morgan-diagram]"),
  )) {
    const spec = diagramSpecFromAttr(
      el.getAttribute("data-morgan-diagram") ?? "",
    );
    if (!spec) {
      el.outerHTML = `<p><em>Diagram could not be rendered.</em></p>`;
      continue;
    }
    try {
      const rendered = await renderMermaidSvg(spec.code, {
        theme: "default",
      });
      if ("error" in rendered) throw new Error(rendered.error);
      const { dataUrl, width, height } = await mermaidSvgToPngDataUrl(
        rendered.svg,
      );
      const maxWidth =
        target === "word" ? WORD_CHART_WIDTH_PX : PRINT_CONTENT_WIDTH_PX;
      const displayWidth = Math.min(maxWidth, width);
      const displayHeight = Math.round((displayWidth * height) / width);
      const imgTag =
        target === "word"
          ? `<img src="${dataUrl}" width="${displayWidth}" height="${displayHeight}" style="width:${displayWidth}px;height:${displayHeight}px;" />`
          : `<img src="${dataUrl}" style="width:${displayWidth}px;height:auto;max-width:100%;" />`;
      el.outerHTML = `<figure style="margin:12px 0;">
  <figcaption style="font-size:12px;font-weight:600;margin:0 0 6px;">${escapeHtml(spec.title)}</figcaption>
  ${imgTag}
</figure>`;
    } catch {
      el.outerHTML = `<p><em>Diagram could not be rendered.</em></p>`;
    }
  }

  // Charts — render offscreen with recharts, capture to a PNG.
  const chartEls = Array.from(root.querySelectorAll("div[data-morgan-chart]"));
  if (chartEls.length > 0) {
    const { default: html2canvas } = await import("html2canvas");
    for (const el of chartEls) {
      const spec = chartSpecFromAttr(
        el.getAttribute("data-morgan-chart") ?? "",
      );
      if (!spec) {
        el.outerHTML = `<p><em>Chart could not be rendered.</em></p>`;
        continue;
      }
      try {
        const dataUrl = await chartSpecToPngDataUrl(spec, html2canvas);
        const wordHeight = Math.round(
          (WORD_CHART_WIDTH_PX * PRINT_CHART_HEIGHT_PX) /
            PRINT_CONTENT_WIDTH_PX,
        );
        const imgTag =
          target === "word"
            ? `<img src="${dataUrl}" width="${WORD_CHART_WIDTH_PX}" height="${wordHeight}" style="width:${WORD_CHART_WIDTH_PX}px;height:${wordHeight}px;" />`
            : `<img src="${dataUrl}" style="width:100%;height:auto;" />`;
        el.outerHTML = `<figure style="margin:12px 0;">
  <figcaption style="font-size:12px;font-weight:600;margin:0 0 6px;">${escapeHtml(spec.title)}</figcaption>
  ${imgTag}
</figure>`;
      } catch {
        el.outerHTML = `<p><em>Chart could not be rendered.</em></p>`;
      }
    }
  }

  return root.innerHTML;
}

/**
 * Render a ChartSpec offscreen via `ChartRenderer` (animation disabled) and
 * capture it to a PNG data URL with html2canvas.
 */
async function chartSpecToPngDataUrl(
  spec: import("@cvx/lib/charts/spec").ChartSpec,
  html2canvas: typeof import("html2canvas").default,
): Promise<string> {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-99999px";
  host.style.top = "0";
  host.style.width = `${PRINT_CONTENT_WIDTH_PX}px`;
  host.style.height = `${PRINT_CHART_HEIGHT_PX}px`;
  host.style.background = "#ffffff";
  document.body.appendChild(host);

  const reactRoot = createRoot(host);
  try {
    reactRoot.render(
      createElement(ChartRenderer, {
        spec,
        height: PRINT_CHART_HEIGHT_PX,
        animated: false,
      }),
    );
    // Let recharts lay out its SVG before capture.
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
    await new Promise((r) => setTimeout(r, 80));

    const canvas = await html2canvas(host, {
      scale: 2,
      backgroundColor: "#ffffff",
      width: PRINT_CONTENT_WIDTH_PX,
      height: PRINT_CHART_HEIGHT_PX,
      windowWidth: PRINT_CONTENT_WIDTH_PX,
    });
    return canvas.toDataURL("image/png");
  } finally {
    reactRoot.unmount();
    host.remove();
  }
}
